import { deleteProjectSetImages } from '@/features/files/utils/storage-service';
import type { ProjectScope } from './project-scope';
import { supersedeProjectWritesForConflictUnit } from './project-write-outbox';

type PendingSetDeleteCleanup = () => void | Promise<void>;
type PendingSetDeleteFailureHandler = (error: unknown) => void;

const pendingSetWriteKeys = new Set<string>();
const pendingSetDeleteKeys = new Set<string>();
const pendingSetDeleteCleanups = new Map<string, PendingSetDeleteCleanup>();
const pendingSetDeleteFailureHandlers = new Map<string, PendingSetDeleteFailureHandler>();
const pendingSetDeleteCleanupKeys = new Set<string>();
const runningSetDeleteCleanupKeys = new Set<string>();
const PENDING_SET_DELETE_CLEANUPS_STORAGE_KEY = 'on-book-pro:pending-set-delete-cleanups:v1';
let didHydratePendingSetDeleteCleanupKeys = false;

function setWriteKey(scope: ProjectScope, setId: string): string {
    return `${scope.orgId}/${scope.projectId}/${setId}`;
}

function setScopePrefix(scope: ProjectScope): string {
    return `${scope.orgId}/${scope.projectId}/`;
}

function setIdFromKey(scope: ProjectScope, key: string): string | null {
    const prefix = setScopePrefix(scope);
    return key.startsWith(prefix) ? key.slice(prefix.length) : null;
}

function scopeFromKey(key: string): { scope: ProjectScope; setId: string } | null {
    const [orgId, projectId, ...setIdParts] = key.split('/');
    const setId = setIdParts.join('/');
    if (!orgId || !projectId || !setId) {
        return null;
    }
    return { scope: { orgId, projectId }, setId };
}

function storageAvailable(): boolean {
    return typeof globalThis.localStorage !== 'undefined';
}

function hydratePendingSetDeleteCleanupKeys(): void {
    if (didHydratePendingSetDeleteCleanupKeys) {
        return;
    }
    didHydratePendingSetDeleteCleanupKeys = true;
    if (!storageAvailable()) {
        return;
    }

    try {
        const rawValue = globalThis.localStorage.getItem(PENDING_SET_DELETE_CLEANUPS_STORAGE_KEY);
        const keys: unknown = rawValue ? JSON.parse(rawValue) : [];
        if (Array.isArray(keys)) {
            for (const key of keys) {
                if (typeof key === 'string' && scopeFromKey(key)) {
                    pendingSetDeleteCleanupKeys.add(key);
                }
            }
        }
    } catch {
        pendingSetDeleteCleanupKeys.clear();
    }
}

function persistPendingSetDeleteCleanupKeys(): void {
    if (!storageAvailable()) {
        return;
    }

    try {
        const keys = Array.from(pendingSetDeleteCleanupKeys).toSorted();
        if (keys.length === 0) {
            globalThis.localStorage.removeItem(PENDING_SET_DELETE_CLEANUPS_STORAGE_KEY);
            return;
        }
        globalThis.localStorage.setItem(PENDING_SET_DELETE_CLEANUPS_STORAGE_KEY, JSON.stringify(keys));
    } catch {
        // Local persistence is best-effort; in-memory cleanup still covers the active session.
    }
}

function deletePendingSetCleanupKey(key: string): void {
    pendingSetDeleteCleanups.delete(key);
    pendingSetDeleteFailureHandlers.delete(key);
    pendingSetDeleteCleanupKeys.delete(key);
    persistPendingSetDeleteCleanupKeys();
}

export function markPendingSetWrite(scope: ProjectScope, setId: string): void {
    hydratePendingSetDeleteCleanupKeys();
    const key = setWriteKey(scope, setId);
    pendingSetWriteKeys.add(key);
    pendingSetDeleteKeys.delete(key);
    deletePendingSetCleanupKey(key);
}

export function clearPendingSetWrite(scope: ProjectScope, setId: string): void {
    pendingSetWriteKeys.delete(setWriteKey(scope, setId));
}

export function isPendingSetWrite(scope: ProjectScope, setId: string): boolean {
    return pendingSetWriteKeys.has(setWriteKey(scope, setId));
}

export function markPendingSetDelete(scope: ProjectScope, setId: string): void {
    pendingSetDeleteKeys.add(setWriteKey(scope, setId));
    pendingSetWriteKeys.delete(setWriteKey(scope, setId));
    void supersedeProjectWritesForConflictUnit({
        scope,
        conflictUnit: `sets/${setId}`,
        kinds: ['patchEntity'],
        expectedStatuses: ['pending', 'retryable', 'conflict_review'],
        reason: 'set-deleted-before-pending-write-applied',
    }).catch(() => undefined);
}

export function markPendingSetDeleteCleanup(
    scope: ProjectScope,
    setId: string,
    cleanup: PendingSetDeleteCleanup,
    onFailure?: PendingSetDeleteFailureHandler
): void {
    hydratePendingSetDeleteCleanupKeys();
    const key = setWriteKey(scope, setId);
    pendingSetDeleteCleanupKeys.add(key);
    pendingSetDeleteCleanups.set(key, cleanup);
    if (onFailure) {
        pendingSetDeleteFailureHandlers.set(key, onFailure);
    }
    persistPendingSetDeleteCleanupKeys();
}

export function isPendingSetDelete(scope: ProjectScope, setId: string): boolean {
    return pendingSetDeleteKeys.has(setWriteKey(scope, setId));
}

function runPendingSetDeleteCleanup(key: string): void {
    if (runningSetDeleteCleanupKeys.has(key)) {
        return;
    }

    const parsedKey = scopeFromKey(key);
    if (!parsedKey) {
        deletePendingSetCleanupKey(key);
        return;
    }

    runningSetDeleteCleanupKeys.add(key);
    const cleanup = pendingSetDeleteCleanups.get(key)
        ?? (() => deleteProjectSetImages(parsedKey.scope, parsedKey.setId));
    void Promise.resolve()
        .then(cleanup)
        .then(() => {
            deletePendingSetCleanupKey(key);
        })
        .catch((error: unknown) => {
            pendingSetDeleteFailureHandlers.get(key)?.(error);
        })
        .finally(() => {
            runningSetDeleteCleanupKeys.delete(key);
        });
}

export function clearPendingSetDeletesMissingFromRemote(
    scope: ProjectScope,
    remoteSetIds: ReadonlySet<string>
): void {
    hydratePendingSetDeleteCleanupKeys();
    const keys = new Set([...pendingSetDeleteKeys, ...pendingSetDeleteCleanupKeys]);
    for (const key of Array.from(keys)) {
        const setId = setIdFromKey(scope, key);
        if (setId && !remoteSetIds.has(setId)) {
            pendingSetDeleteKeys.delete(key);
            runPendingSetDeleteCleanup(key);
        }
    }
}

export function clearPendingSetWrites(): void {
    pendingSetWriteKeys.clear();
    pendingSetDeleteKeys.clear();
    pendingSetDeleteCleanups.clear();
    pendingSetDeleteFailureHandlers.clear();
    pendingSetDeleteCleanupKeys.clear();
    runningSetDeleteCleanupKeys.clear();
    persistPendingSetDeleteCleanupKeys();
}
